/**
 * Service for managing application settings
 */

// Define the shape of our app settings
export interface AppSettings {
  showSOPFlaggingFeature: boolean;
  autoGenerateSummaries: boolean;
  // Add other app-wide settings here as needed
}

/**
 * Service for managing application settings
 */
export const appSettingsService = {
  /**
   * Get all application settings
   * @returns Promise resolving to the application settings
   */
  async getSettings(): Promise<AppSettings> {
    try {
      const response = await fetch('/.netlify/functions/sops/settings', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Return default settings instead of throwing error
        console.warn('SOP settings endpoint not available, using defaults');
        return {
          showSOPFlaggingFeature: true,
          autoGenerateSummaries: true
        };
      }

      const data = await response.json();
      
      if (data.success && data.settings) {
        return data.settings;
      }
      
      // Return default settings for invalid response
      console.warn('Invalid SOP settings response, using defaults');
      return {
        showSOPFlaggingFeature: true,
        autoGenerateSummaries: true
      };
    } catch (error) {
      // Return default settings on error
      console.warn('Failed to fetch SOP settings, using defaults:', error);
      return {
        showSOPFlaggingFeature: true,
        autoGenerateSummaries: true
      };
    }
  },

  /**
   * Update application settings
   * @param settings The settings to update
   * @returns Promise resolving when the update is complete
   */
  async updateSettings(settings: Partial<AppSettings>): Promise<void> {
    try {
      const response = await fetch('/.netlify/functions/sops/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          settings
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update settings: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to update settings');
      }
    } catch (error) {
      
      throw error;
    }
  },

  /**
   * Toggle the SOP flagging feature
   * @param enabled Whether the feature should be enabled
   * @returns Promise resolving when the update is complete
   */
  async toggleSOPFlaggingFeature(enabled: boolean): Promise<void> {
    return this.updateSettings({
      showSOPFlaggingFeature: enabled
    });
  },

  /**
   * Toggle the auto-generate summaries feature
   * @param enabled Whether the feature should be enabled
   * @returns Promise resolving when the update is complete
   */
  async toggleAutoGenerateSummaries(enabled: boolean): Promise<void> {
    return this.updateSettings({
      autoGenerateSummaries: enabled
    });
  }
};

export default appSettingsService;